import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import Button from '../../Common/Button/Button';
import Input from '../../Common/Input/Input';
import { ErrorMessage } from '../../Common/ErrorMessage/ErrorMessage';
import { COLUMN_TYPES, MESSAGES } from '../../Utils/constants';
import { formatTableNameForBackend, generateColumnId } from '../../Utils/tableHelpers';
import TablesService from '../../../../../AppSettings/services/TablesService';
import './TableForm.css';

/**
 * Excel-like Table Builder Component
 * Provides streamlined grid interface for creating AND editing custom tables
 */
const TableBuilder = ({ 
  onClose, 
  onTableCreated, 
  editMode = false, 
  existingTable = null,
  onTableUpdated = null 
}) => {
  const gridContainerRef = useRef(null);
  const [tableName, setTableName] = useState("");
  const [gridData, setGridData] = useState([]);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editingHeader, setEditingHeader] = useState(null);
  const [cellChecklistBuilder, setCellChecklistBuilder] = useState(null); // { rowIndex, columnId, options: [] }
  const [hasRestoredCache, setHasRestoredCache] = useState(false);
  const [originalTableData, setOriginalTableData] = useState(null); // Store original data for revert
  const [draggedRowIndex, setDraggedRowIndex] = useState(null);
  const [dragOverRowIndex, setDragOverRowIndex] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);

  const SCROLL_EDGE_THRESHOLD = 40;
  const SCROLL_STEP = 6;
  const autoScrollRef = useRef({
    frameId: null,
    direction: 0
  });

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current.frameId) {
      cancelAnimationFrame(autoScrollRef.current.frameId);
      autoScrollRef.current.frameId = null;
    }
    autoScrollRef.current.direction = 0;
  }, []);

  const startAutoScroll = useCallback((direction) => {
    if (!gridContainerRef.current) return;
    if (autoScrollRef.current.direction === direction) return;

    stopAutoScroll();
    if (direction === 0) return;

    autoScrollRef.current.direction = direction;

    const step = () => {
      if (!gridContainerRef.current || autoScrollRef.current.direction === 0) {
        stopAutoScroll();
        return;
      }
      gridContainerRef.current.scrollBy({ top: direction * SCROLL_STEP, behavior: 'auto' });
      autoScrollRef.current.frameId = requestAnimationFrame(step);
    };

    autoScrollRef.current.frameId = requestAnimationFrame(step);
  }, [stopAutoScroll]);

  useEffect(() => {
    return () => stopAutoScroll();
  }, [stopAutoScroll]);

  // Cache key: different for create vs edit mode (include table ID for edits)
  const CACHE_KEY = editMode && existingTable 
    ? `table_builder_edit_${existingTable.id}` 
    : editMode 
    ? null 
    : 'table_builder_cache';

  // Cache management functions
  const loadCachedData = () => {
    if (!CACHE_KEY) return null;
    
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        // Check if cache is not too old (e.g., 24 hours)
        const cacheAge = Date.now() - (data.timestamp || 0);
        const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours
        
        if (cacheAge < MAX_CACHE_AGE) {
          return data;
        } else {
          // Clear old cache
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading cached data:', error);
    }
    return null;
  };

  const saveCachedData = useCallback(() => {
    if (!CACHE_KEY) return;
    
    try {
      const dataToCache = {
        tableName,
        columnHeaders,
        gridData,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
    } catch (error) {
      console.error('Error saving cached data:', error);
    }
  }, [tableName, columnHeaders, gridData, CACHE_KEY]);

  const clearCachedData = () => {
    if (!CACHE_KEY) return;
    
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error('Error clearing cached data:', error);
    }
  };

  // Load existing table data or cached data on mount
  useEffect(() => {
    if (editMode && existingTable) {
      // Load existing table for editing
      const originalName = existingTable.table_name || "";
      const columns = Array.isArray(existingTable.column_value) 
        ? existingTable.column_value 
        : (typeof existingTable.column_value === 'string' 
          ? JSON.parse(existingTable.column_value) 
          : []);
      const rows = Array.isArray(existingTable.rows_value) 
        ? existingTable.rows_value 
        : (typeof existingTable.rows_value === 'string' 
          ? JSON.parse(existingTable.rows_value) 
          : []);
      
      const originalData = {
        tableName: originalName,
        columnHeaders: columns,
        gridData: rows.length > 0 ? rows : [createEmptyRow(columns)]
      };
      
      // Store original data for revert functionality
      setOriginalTableData(originalData);
      
      // Check for cached draft edits
      const cachedDraft = loadCachedData();
      if (cachedDraft) {
        // Restore draft edits
        setTableName(cachedDraft.tableName || originalName);
        setColumnHeaders(cachedDraft.columnHeaders || columns);
        setGridData(cachedDraft.gridData || originalData.gridData);
        setHasRestoredCache(true);
      } else {
        // Load original data
        setTableName(originalName);
        setColumnHeaders(columns);
        setGridData(originalData.gridData);
      }
    } else {
      // Create mode - try to load cached data
      const cachedData = loadCachedData();
      if (cachedData) {
        setTableName(cachedData.tableName || "");
        setColumnHeaders(cachedData.columnHeaders || []);
        setGridData(cachedData.gridData || []);
        setHasRestoredCache(true);
      } else {
        initializeDefaultGrid();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, existingTable]);

  // Auto-save to cache whenever data changes
  useEffect(() => {
    // Don't save on initial load or if nothing has changed
    if (tableName || columnHeaders.length > 0 || gridData.length > 0) {
      const timeoutId = setTimeout(() => {
        saveCachedData();
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [tableName, columnHeaders, gridData, saveCachedData]);

  // Initialize grid with proper data types for each column
  // This function is used when loading existing table data from database
  // eslint-disable-next-line no-unused-vars
  const initializeGridWithDataTypes = (columns, rows) => {
    return rows.map(row => {
      const formattedRow = {};
      columns.forEach(col => {
        let cellValue = row[col.id];
        
        switch (col.type) {
          case COLUMN_TYPES.CHECKBOX:
            // Ensure boolean value
            formattedRow[col.id] = cellValue === true || cellValue === 'true';
            break;
            
          case COLUMN_TYPES.CHECKLIST:
            // Ensure array format
            if (Array.isArray(cellValue)) {
              formattedRow[col.id] = cellValue;
            } else {
              // Initialize with empty array for the number of options
              const optionsCount = col.checklistOptions ? col.checklistOptions.length : 0;
              formattedRow[col.id] = new Array(optionsCount).fill(false);
            }
            break;
            
          case COLUMN_TYPES.CELL_CHECKLIST:
            // For cell-specific checklists, always save as text (comma-separated selected options)
            formattedRow[col.id] = cellValue || '';
            
            // Load cell-specific checklist options if they exist
            const cellChecklistOptions = row[`${col.id}_checklistOptions`];
            if (cellChecklistOptions && Array.isArray(cellChecklistOptions)) {
              formattedRow[`${col.id}_checklistOptions`] = cellChecklistOptions;
            }
            break;
            
          default:
            formattedRow[col.id] = cellValue || '';
        }
      });
      return formattedRow;
    });
  };

  // Helper to create an empty row based on columns
  const createEmptyRow = (columns) => {
    const newRow = {};
    columns.forEach(col => {
      let defaultValue;
      switch (col.type) {
        case COLUMN_TYPES.CHECKBOX:
          defaultValue = false;
          break;
        case COLUMN_TYPES.CHECKLIST:
          defaultValue = new Array(col.checklistOptions ? col.checklistOptions.length : 0).fill(false);
          break;
        case COLUMN_TYPES.CELL_CHECKLIST:
          defaultValue = '';
          break;
        default:
          defaultValue = '';
      }
      newRow[col.id] = defaultValue;
    });
    return newRow;
  };

  const initializeDefaultGrid = () => {
    const defaultColumns = 3;
    const defaultRows = 3;
    
    // Create column headers
    const headers = Array.from({ length: defaultColumns }, (_, i) => ({
      id: `col${i + 1}`,
      label: `Column ${i + 1}`,
      type: COLUMN_TYPES.TEXT,
      readonly: false
    }));
    
    // Create grid data
    const grid = Array.from({ length: defaultRows }, () => 
      headers.reduce((row, col) => ({ ...row, [col.id]: '' }), {})
    );
    
    setColumnHeaders(headers);
    setGridData(grid);
  };

  // Grid Management
  const handleAddColumn = () => {
    const newColumnId = generateColumnId(columnHeaders);
    const newColumn = {
      id: newColumnId,
      label: `Column ${columnHeaders.length + 1}`,
      type: COLUMN_TYPES.TEXT,
      readonly: false
    };
    
    // Add new column to headers
    const updatedHeaders = [...columnHeaders, newColumn];
    setColumnHeaders(updatedHeaders);
    
    // Add default values for the new column to all existing rows
    const updatedGrid = gridData.map(row => {
      let defaultValue;
      switch (newColumn.type) {
        case COLUMN_TYPES.CHECKBOX:
          defaultValue = false;
          break;
        case COLUMN_TYPES.CHECKLIST:
          defaultValue = new Array(newColumn.checklistOptions ? newColumn.checklistOptions.length : 0).fill(false);
          break;
        case COLUMN_TYPES.CELL_CHECKLIST:
          // Initialize with empty string for cell-specific checklists (will be text field until options are created)
          defaultValue = '';
          break;
        default:
          defaultValue = '';
      }
      return { ...row, [newColumnId]: defaultValue };
    });
    setGridData(updatedGrid);
  };

  const handleAddRow = () => {
    const newRow = columnHeaders.reduce((row, col) => {
      let defaultValue;
      switch (col.type) {
        case COLUMN_TYPES.CHECKBOX:
          defaultValue = false;
          break;
        case COLUMN_TYPES.CHECKLIST:
          // Initialize with false for each option
          defaultValue = new Array(col.checklistOptions ? col.checklistOptions.length : 0).fill(false);
          break;
        case COLUMN_TYPES.CELL_CHECKLIST:
          // Initialize with empty string for cell-specific checklists (will be text field until options are created)
          defaultValue = '';
          break;
        default:
          defaultValue = '';
      }
      return { ...row, [col.id]: defaultValue };
    }, {});
    setGridData([...gridData, newRow]);
  };

  const handleRemoveColumn = (columnIndex) => {
    if (columnHeaders.length <= 1) return; // Keep at least one column
    
    const columnToRemove = columnHeaders[columnIndex];
    const updatedHeaders = columnHeaders.filter((_, i) => i !== columnIndex);
    setColumnHeaders(updatedHeaders);
    
    // Remove column data from all rows
    const updatedGrid = gridData.map(row => {
      const newRow = { ...row };
      delete newRow[columnToRemove.id];
      return newRow;
    });
    setGridData(updatedGrid);
  };

  const handleRemoveRow = (rowIndex) => {
    if (gridData.length <= 1) return; // Keep at least one row
    
    const updatedGrid = gridData.filter((_, i) => i !== rowIndex);
    setGridData(updatedGrid);
  };

  const handleRowDragStart = (event, rowIndex) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(rowIndex));
    setDraggedRowIndex(rowIndex);
    setDragOverRowIndex(null);
    setDragOverPosition(null);
  };

  const handleRowDragOver = (event, rowIndex) => {
    event.preventDefault();
    if (draggedRowIndex === null || rowIndex === draggedRowIndex) {
      setDragOverRowIndex(null);
      setDragOverPosition(null);
      return;
    }

    let scrollDirection = 0;
    if (gridContainerRef.current) {
      const gridRect = gridContainerRef.current.getBoundingClientRect();
      if (event.clientY > gridRect.bottom - SCROLL_EDGE_THRESHOLD) {
        scrollDirection = 1;
      } else if (event.clientY < gridRect.top + SCROLL_EDGE_THRESHOLD) {
        scrollDirection = -1;
      }
    }
    startAutoScroll(scrollDirection);

    const { top, height } = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - top;
    const position = offsetY < height / 2 ? 'above' : 'below';
    event.dataTransfer.dropEffect = 'move';

    if (dragOverRowIndex !== rowIndex || dragOverPosition !== position) {
      setDragOverRowIndex(rowIndex);
      setDragOverPosition(position);
    }
  };

  const handleRowDragLeave = (event, rowIndex) => {
    event.preventDefault();
    if (dragOverRowIndex === rowIndex) {
      setDragOverRowIndex(null);
      setDragOverPosition(null);
    }
    startAutoScroll(0);
  };

  const handleRowDrop = (event, rowIndex) => {
    event.preventDefault();
    if (draggedRowIndex === null || draggedRowIndex === rowIndex) {
      setDraggedRowIndex(null);
      setDragOverRowIndex(null);
      setDragOverPosition(null);
      return;
    }

    const { top, height } = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - top;
    const dropPosition = offsetY < height / 2 ? 'above' : 'below';

    let targetIndex = dropPosition === 'below' ? rowIndex + 1 : rowIndex;
    const updatedGrid = [...gridData];
    const [movedRow] = updatedGrid.splice(draggedRowIndex, 1);

    if (draggedRowIndex < targetIndex) {
      targetIndex -= 1;
    }

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex > updatedGrid.length) targetIndex = updatedGrid.length;

    updatedGrid.splice(targetIndex, 0, movedRow);
    setEditingCell(null);
    setGridData(updatedGrid);
    setDraggedRowIndex(null);
    setDragOverRowIndex(null);
    setDragOverPosition(null);
    startAutoScroll(0);
  };

  const handleRowDragEnd = () => {
    setDraggedRowIndex(null);
    setDragOverRowIndex(null);
    setDragOverPosition(null);
    startAutoScroll(0);
  };

  // Cell Editing
  const handleCellClick = (rowIndex, columnId, e) => {
    // Check if column is readonly
    const column = columnHeaders.find(col => col.id === columnId);
    if (column?.readonly) {
      return; // Don't allow editing readonly columns
    }
    
    // Handle different column types
    if (column?.type === COLUMN_TYPES.CHECKBOX) {
      return; // Checkbox columns are handled by their input onChange
    }
    
    if (column?.type === COLUMN_TYPES.CHECKLIST) {
      // For checklist columns, check if it's a right-click or Ctrl+click for cell-specific checklist builder
      if (e?.button === 2 || e?.ctrlKey || e?.metaKey) {
        handleCellChecklistBuilderOpen(rowIndex, columnId, e);
        return;
      }
      // Regular click on checklist column - handle normal checklist item toggle
      return;
    }
    
    if (column?.type === COLUMN_TYPES.CELL_CHECKLIST) {
      // For cell-specific checklist columns, always open the builder on click
      handleCellChecklistBuilderOpen(rowIndex, columnId, e);
      return;
    }
    
    // For other column types, enable inline editing
    setEditingCell({ rowIndex, columnId });
  };

  // Handle checklist item toggle
  const handleChecklistItemToggle = (rowIndex, columnId, optionIndex) => {
    const updatedGrid = [...gridData];
    const currentValue = updatedGrid[rowIndex][columnId] || [];
    const newValue = [...currentValue];
    
    // Toggle the specific option
    if (newValue[optionIndex]) {
      newValue[optionIndex] = !newValue[optionIndex];
    } else {
      // Initialize if doesn't exist
      newValue[optionIndex] = true;
    }
    
    updatedGrid[rowIndex] = {
      ...updatedGrid[rowIndex],
      [columnId]: newValue
    };
    setGridData(updatedGrid);
  };

  // Handle cell-specific checklist text toggle (saves as comma-separated string)
  const handleCellChecklistTextToggle = (rowIndex, columnId, option, optionIndex) => {
    const updatedGrid = [...gridData];
    const currentValue = updatedGrid[rowIndex][columnId] || '';
    const selectedOptions = currentValue ? currentValue.split(',').map(opt => opt.trim()) : [];
    
    let newValue;
    if (selectedOptions.includes(option)) {
      // Remove option from selected list
      newValue = selectedOptions.filter(opt => opt !== option).join(', ');
    } else {
      // Add option to selected list
      newValue = [...selectedOptions, option].join(', ');
    }
    
    updatedGrid[rowIndex] = {
      ...updatedGrid[rowIndex],
      [columnId]: newValue
    };
    setGridData(updatedGrid);
  };

  const handleCellChange = (rowIndex, columnId, value) => {
    const updatedGrid = [...gridData];
    updatedGrid[rowIndex] = {
      ...updatedGrid[rowIndex],
      [columnId]: value
    };
    setGridData(updatedGrid);
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  // Header Editing
  const handleHeaderClick = (columnIndex) => {
    setEditingHeader(columnIndex);
  };

  const handleHeaderChange = (columnIndex, field, value) => {
    const updatedHeaders = [...columnHeaders];
    const oldColumn = updatedHeaders[columnIndex];
    const newColumn = {
      ...oldColumn,
      [field]: value
    };
    updatedHeaders[columnIndex] = newColumn;
    
    // If column type changed, update existing row data to match new type
    if (field === 'type' && value !== oldColumn.type) {
      const updatedGrid = gridData.map(row => {
        const newRow = { ...row };
        let newValue;
        
        switch (value) {
          case COLUMN_TYPES.CHECKBOX:
            // Convert existing value to boolean
            const existingValue = row[oldColumn.id];
            newValue = existingValue === true || existingValue === 'true' || existingValue === '1';
            break;
            
          case COLUMN_TYPES.CHECKLIST:
            // Initialize with empty checklist
            newValue = new Array(newColumn.checklistOptions ? newColumn.checklistOptions.length : 0).fill(false);
            break;
            
          case COLUMN_TYPES.CELL_CHECKLIST:
            // Initialize with empty string for cell-specific checklists (will be text field until options are created)
            newValue = '';
            break;
            
          default:
            // Convert to string for text types
            if (Array.isArray(row[oldColumn.id])) {
              newValue = ''; // Convert arrays to empty string
            } else if (typeof row[oldColumn.id] === 'boolean') {
              newValue = row[oldColumn.id] ? 'true' : 'false'; // Convert booleans to string
            } else {
              newValue = row[oldColumn.id] || '';
            }
        }
        
        newRow[oldColumn.id] = newValue;
        return newRow;
      });
      setGridData(updatedGrid);
    }
    
    // If checklist options changed, update existing row data arrays
    if (field === 'checklistOptions' && oldColumn.type === COLUMN_TYPES.CHECKLIST) {
      const updatedGrid = gridData.map(row => {
        const newRow = { ...row };
        const currentArray = row[oldColumn.id] || [];
        const newOptionsLength = Array.isArray(value) ? value.length : 0;
        
        // Resize array to match new options count
        if (newOptionsLength > currentArray.length) {
          // Add false values for new options
          newRow[oldColumn.id] = [...currentArray, ...new Array(newOptionsLength - currentArray.length).fill(false)];
        } else if (newOptionsLength < currentArray.length) {
          // Trim array to new size
          newRow[oldColumn.id] = currentArray.slice(0, newOptionsLength);
        } else {
          // Keep existing array
          newRow[oldColumn.id] = currentArray;
        }
        
        return newRow;
      });
      setGridData(updatedGrid);
    }
    
    setColumnHeaders(updatedHeaders);
  };

  const handleHeaderBlur = (e) => {
    // Only close if the blur is not going to another element within the same header edit group
    setTimeout(() => {
      const activeElement = document.activeElement;
      const editGroup = e?.target?.closest('.header-edit-group');
      const isStillInEditGroup = editGroup && editGroup.contains(activeElement);
      
      if (!isStillInEditGroup) {
        setEditingHeader(null);
      }
    }, 0);
  };

  // Handle readonly toggle with smooth interaction
  const handleReadonlyToggle = (columnIndex, e) => {
    e.stopPropagation(); // Prevent triggering header edit mode
    const updatedHeaders = [...columnHeaders];
    updatedHeaders[columnIndex] = {
      ...updatedHeaders[columnIndex],
      readonly: !updatedHeaders[columnIndex].readonly
    };
    setColumnHeaders(updatedHeaders);
  };

  // Cell-specific checklist builder functions
  const handleCellChecklistBuilderOpen = (rowIndex, columnId, e) => {
    e.stopPropagation();
    const currentOptions = gridData[rowIndex]?.[`${columnId}_checklistOptions`] || [];
    setCellChecklistBuilder({
      rowIndex,
      columnId,
      options: [...currentOptions]
    });
  };

  const handleCellChecklistBuilderClose = () => {
    setCellChecklistBuilder(null);
  };

  const handleCellChecklistOptionAdd = () => {
    if (!cellChecklistBuilder) return;
    setCellChecklistBuilder({
      ...cellChecklistBuilder,
      options: [...cellChecklistBuilder.options, '']
    });
  };

  const handleCellChecklistOptionChange = (optionIndex, value) => {
    if (!cellChecklistBuilder) return;
    const newOptions = [...cellChecklistBuilder.options];
    newOptions[optionIndex] = value;
    setCellChecklistBuilder({
      ...cellChecklistBuilder,
      options: newOptions
    });
  };

  const handleCellChecklistOptionRemove = (optionIndex) => {
    if (!cellChecklistBuilder) return;
    const newOptions = [...cellChecklistBuilder.options];
    newOptions.splice(optionIndex, 1);
    setCellChecklistBuilder({
      ...cellChecklistBuilder,
      options: newOptions
    });
  };

  const handleCellChecklistSave = () => {
    if (!cellChecklistBuilder) return;
    
    const { rowIndex, columnId, options } = cellChecklistBuilder;
    const updatedGrid = [...gridData];
    
    // Save the checklist options for this specific cell
    updatedGrid[rowIndex] = {
      ...updatedGrid[rowIndex],
      [`${columnId}_checklistOptions`]: options.filter(opt => opt.trim() !== '')
    };
    
    // Initialize cell value as empty string if not exists (will be populated when user selects options)
    if (!updatedGrid[rowIndex][columnId]) {
      updatedGrid[rowIndex][columnId] = '';
    }
    
    setGridData(updatedGrid);
    setCellChecklistBuilder(null);
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    
    if (!tableName.trim()) {
      setErrorMessage(MESSAGES.ERRORS.NO_TABLE_NAME);
      return;
    }
    
    if (columnHeaders.length === 0) {
      setErrorMessage(MESSAGES.ERRORS.NO_COLUMNS);
      return;
    }

    // Validate checklist columns have at least one option
    const invalidChecklists = columnHeaders.filter(col => 
      col.type === COLUMN_TYPES.CHECKLIST && 
      (!col.checklistOptions || col.checklistOptions.filter(opt => opt.trim() !== '').length === 0)
    );
    
    if (invalidChecklists.length > 0) {
      setErrorMessage(`Checklist columns must have at least one option: ${invalidChecklists.map(col => col.label).join(', ')}`);
      return;
    }

    setIsLoading(true);
    
    try {
      const formattedName = editMode ? tableName : formatTableNameForBackend(tableName);
      
      const tableData = {
        table_name: formattedName,
        column_value: columnHeaders.map(col => {
          const columnData = {
            id: col.id,
            label: col.label,
            type: col.type,
            readonly: col.readonly || false,
            isActions: false
          };
          
          // Add checkbox-specific properties
          if (col.type === COLUMN_TYPES.CHECKBOX && col.checkboxLabel) {
            columnData.checkboxLabel = col.checkboxLabel;
          }
          
          // Add checklist-specific properties
          if (col.type === COLUMN_TYPES.CHECKLIST && col.checklistOptions) {
            columnData.checklistOptions = col.checklistOptions.filter(option => option.trim() !== '');
          }
          
          return columnData;
        }),
        rows_value: gridData.map(row => {
          const formattedRow = {};
          columnHeaders.forEach(col => {
            let cellValue = row[col.id];
            
            // Handle different column types
            switch (col.type) {
              case COLUMN_TYPES.CHECKBOX:
                // Ensure boolean value for checkboxes
                formattedRow[col.id] = cellValue === true || cellValue === 'true';
                break;
                
              case COLUMN_TYPES.CHECKLIST:
                // Ensure array of booleans for checklists
                if (Array.isArray(cellValue)) {
                  formattedRow[col.id] = cellValue;
                } else {
                  // Initialize empty array if no value exists
                  formattedRow[col.id] = [];
                }
                break;
                
              case COLUMN_TYPES.CELL_CHECKLIST:
                // Always save cell-specific checklists as text (comma-separated selected options)
                formattedRow[col.id] = cellValue || '';
                
                // Also save cell-specific checklist options if they exist
                const cellChecklistOptions = row[`${col.id}_checklistOptions`];
                if (cellChecklistOptions && cellChecklistOptions.length > 0) {
                  formattedRow[`${col.id}_checklistOptions`] = cellChecklistOptions;
                }
                break;
                
              default:
                // Handle text, number, date, textarea normally
                formattedRow[col.id] = cellValue || '';
            }
          });
          return formattedRow;
        })
      };

      if (editMode && existingTable) {
        // Update existing table - use TablesService for custom tables
        await TablesService.updateCustomTableColumnsRows(existingTable.id, {
          table_name: tableData.table_name,
          column_value: tableData.column_value,
          rows_value: tableData.rows_value
        });
        clearCachedData(); // Clear cache after successful update
        if (onTableUpdated) onTableUpdated();
      } else {
        // Create new table
        await TablesService.createCustomTable(tableData);
        clearCachedData(); // Clear cache after successful creation
        if (onTableCreated) onTableCreated();
      }
      
      onClose();
    } catch (error) {
      console.error(editMode ? "Error updating table:" : "Error creating table:", error);
      setErrorMessage(editMode ? "Error updating table. Check console for details." : MESSAGES.ERRORS.CREATE_TABLE);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle close with confirmation if there's unsaved work
  const handleClose = () => {
    // Check if there are changes compared to original (for edit mode) or any meaningful work (for create mode)
    let hasUnsavedWork = false;
    
    if (editMode && originalTableData) {
      // In edit mode, check if anything changed from original
      const dataChanged = 
        tableName !== originalTableData.tableName ||
        JSON.stringify(columnHeaders) !== JSON.stringify(originalTableData.columnHeaders) ||
        JSON.stringify(gridData) !== JSON.stringify(originalTableData.gridData);
      hasUnsavedWork = dataChanged;
    } else {
      // In create mode, check if any meaningful work was done
      hasUnsavedWork = tableName.trim() || 
                      columnHeaders.some(col => col.label !== `Column ${columnHeaders.indexOf(col) + 1}`) ||
                      gridData.some(row => Object.values(row).some(val => val !== ''));
    }
    
    if (hasUnsavedWork) {
      const message = editMode
        ? 'You have unsaved changes. Your edits have been auto-saved as a draft and will be restored when you open this table again.\n\nDo you want to close?'
        : 'You have unsaved changes. Your work has been auto-saved and will be restored when you open the table builder again.\n\nDo you want to close?';
      
      const confirmClose = window.confirm(message);
      if (confirmClose) {
        onClose();
      }
    } else {
      clearCachedData(); // Clear cache if no meaningful work was done
      onClose();
    }
  };

  // Clear cache and start fresh
  const handleClearCache = () => {
    const confirmClear = window.confirm(
      editMode 
        ? 'Are you sure you want to discard your draft changes and revert to the original table data?'
        : 'Are you sure you want to discard your saved work and start fresh?'
    );
    if (confirmClear) {
      clearCachedData();
      if (editMode && originalTableData) {
        // Revert to original table data
        setTableName(originalTableData.tableName);
        setColumnHeaders(originalTableData.columnHeaders);
        setGridData(originalTableData.gridData);
      } else {
        // Start fresh
        setTableName("");
        initializeDefaultGrid();
      }
      setHasRestoredCache(false);
    }
  };

  return (
    <div className="table-builder excel-style">
      <form onSubmit={handleSubmit} className="table-builder-form">
        <div className="table-builder-header">
          <h3>        
            {editMode ? 'Edit Table' : 'Create New Table'}
          </h3>
          
          {hasRestoredCache && (
            <div className="cache-restored-message">
              <span className="cache-icon">💾</span>
              <span>
                {editMode 
                  ? 'Your draft changes have been restored. ' 
                  : 'Your previous work has been restored. '}
              </span>
              <button 
                type="button" 
                onClick={handleClearCache}
                className="clear-cache-link"
              >
                {editMode ? 'Revert to original instead?' : 'Start fresh instead?'}
              </button>
            </div>
          )}
          
          {errorMessage && <ErrorMessage message={errorMessage} />}
        </div>
        
        {/* Table Name Input */}
        <div className="table-name-section">
          <Input
            type="text"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            placeholder="Enter table name"
            className="Engtable-table-name-input"
            label="Table Name"
            required
          />
        </div>

        {/* Excel-like Grid Interface */}
        <div className="excel-grid-container">
          <div className="grid-controls">
            <Button
              type="button"
              variant="add"
              icon={faPlus}
              onClick={handleAddColumn}
              size="small"
              title="Add Column"
            >
              Add Column
            </Button>
            
            <Button
              type="button"
              variant="add"
              icon={faPlus}
              onClick={handleAddRow}
              size="small"
              title="Add Row"
            >
              Add Row
            </Button>
          </div>

          <div className="excel-grid" ref={gridContainerRef}>
            <table className="grid-table">
              <thead>
                <tr>
                  <th className="row-number-header">#</th>
                  {columnHeaders.map((column, colIndex) => (
                    <th key={column.id} className="column-header">
                      <div className="header-content">
                        {editingHeader === colIndex ? (
                          <div className="header-edit-group">
                            <Input
                              type="text"
                              value={column.label}
                              onChange={(e) => handleHeaderChange(colIndex, 'label', e.target.value)}
                              onBlur={handleHeaderBlur}
                              onKeyDown={(e) => e.key === 'Enter' && handleHeaderBlur()}
                              autoFocus
                              className="header-input"
                            />
                            <select
                              value={column.type}
                              onChange={(e) => handleHeaderChange(colIndex, 'type', e.target.value)}
                              className="Engtable-column-type-select"
                            >
                              <option value={COLUMN_TYPES.TEXT}>Text</option>
                              <option value={COLUMN_TYPES.NUMBER}>Number</option>
                              <option value={COLUMN_TYPES.DATE}>Date</option>
                              <option value={COLUMN_TYPES.TEXTAREA}>Long Text</option>
                              <option value={COLUMN_TYPES.CHECKBOX}>Checkbox</option>
                              <option value={COLUMN_TYPES.CHECKLIST}>Checklist</option>
                              <option value={COLUMN_TYPES.CELL_CHECKLIST}>Cell-Specific Checklist</option>
                            </select>
                            <div className="column-options">
                              <div className="lock-toggle-container">
                                <button
                                  type="button"
                                  className={`lock-toggle-btn-edit ${column.readonly ? 'locked' : 'unlocked'}`}
                                  onClick={(e) => handleReadonlyToggle(colIndex, e)}
                                  title={column.readonly ? 'Click to unlock (make editable)' : 'Click to lock (make read-only)'}
                                >
                                  <FontAwesomeIcon 
                                    icon={column.readonly ? faLock : faLockOpen} 
                                    size="sm"
                                  />
                                </button>
                                <span className="readonly-label">Read Only</span>
                              </div>
                              {column.type === COLUMN_TYPES.CHECKBOX && (
                                <div className="checkbox-label-config">
                                  <Input
                                    type="text"
                                    value={column.checkboxLabel || ''}
                                    onChange={(e) => handleHeaderChange(colIndex, 'checkboxLabel', e.target.value)}
                                    placeholder="Checkbox label (optional)"
                                    className="checkbox-label-input"
                                  />
                                </div>
                              )}
                              {column.type === COLUMN_TYPES.CHECKLIST && (
                                <div className="checklist-config">
                                  <div className="checklist-options-header">
                                    <span>Checklist Options:</span>
                                    <Button
                                      type="button"
                                      variant="add"
                                      size="small"
                                      onClick={() => {
                                        const currentOptions = column.checklistOptions || [];
                                        const newOptions = [...currentOptions, ''];
                                        handleHeaderChange(colIndex, 'checklistOptions', newOptions);
                                      }}
                                    >
                                      Add Option
                                    </Button>
                                  </div>
                                  {(column.checklistOptions || []).map((option, optionIndex) => (
                                    <div key={optionIndex} className="checklist-option-item">
                                      <Input
                                        type="text"
                                        value={option}
                                        onChange={(e) => {
                                          const newOptions = [...(column.checklistOptions || [])];
                                          newOptions[optionIndex] = e.target.value;
                                          handleHeaderChange(colIndex, 'checklistOptions', newOptions);
                                        }}
                                        placeholder={`Option ${optionIndex + 1}`}
                                        className="checklist-option-input"
                                      />
                                      <Button
                                        type="button"
                                        variant="remove"
                                        size="small"
                                        onClick={() => {
                                          const newOptions = [...(column.checklistOptions || [])];
                                          newOptions.splice(optionIndex, 1);
                                          handleHeaderChange(colIndex, 'checklistOptions', newOptions);
                                        }}
                                        title="Remove option"
                                      >
                                        ×
                                      </Button>
                                    </div>
                                  ))}
                                  {(!column.checklistOptions || column.checklistOptions.length === 0) && (
                                    <div className="checklist-empty-state">
                                      <em>Click "Add Option" to create checklist items</em>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="header-display"
                            onClick={() => handleHeaderClick(colIndex)}
                          >
                            <span className="column-label">{column.label}</span>
                            <span className="column-type">({column.type})</span>
                            {column.readonly && <span className="readonly-badge">Read Only</span>}
                          </div>
                        )}
                        
                        {columnHeaders.length > 1 && (
                          <Button
                            type="button"
                            variant="remove"
                            icon={faTrash}
                            onClick={() => handleRemoveColumn(colIndex)}
                            size="small"
                            className="remove-column-btn"
                            title="Remove column"
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              
              <tbody>
                {gridData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`grid-row ${draggedRowIndex === rowIndex ? 'dragging' : ''} ${dragOverRowIndex === rowIndex ? `drag-over-${dragOverPosition || 'center'}` : ''}`}
                    onDragOver={(e) => handleRowDragOver(e, rowIndex)}
                    onDrop={(e) => handleRowDrop(e, rowIndex)}
                    onDragLeave={(e) => handleRowDragLeave(e, rowIndex)}
                  >
                    <td className="row-number">
                      <div
                        className="row-number-handle"
                        draggable
                        onDragStart={(e) => handleRowDragStart(e, rowIndex)}
                        onDragEnd={handleRowDragEnd}
                        title="Drag row"
                      >
                        <span>{rowIndex + 1}</span>
                      </div>
                      {gridData.length > 1 && (
                        <Button
                          type="button"
                          variant="remove"
                          icon={faTrash}
                          onClick={() => handleRemoveRow(rowIndex)}
                          size="small"
                          className="remove-row-btn"
                          title="Remove row"
                        />
                      )}
                    </td>
                    
                    {columnHeaders.map((column) => {
                      const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnId === column.id;
                      const isReadonly = column.readonly;
                      return (
                        <td 
                          key={column.id} 
                          className={`grid-cell ${isEditing ? 'editing' : ''} ${isReadonly ? 'readonly' : ''} ${column.type === COLUMN_TYPES.CHECKBOX ? 'checkbox-column' : ''} ${column.type === COLUMN_TYPES.CHECKLIST ? 'checklist-column' : ''} ${column.type === COLUMN_TYPES.CELL_CHECKLIST ? 'cell-checklist-column' : ''} ${row[`${column.id}_checklistOptions`] ? 'has-cell-checklist' : ''}`}
                          onClick={(e) => handleCellClick(rowIndex, column.id, e)}
                          onContextMenu={(e) => {
                            if (column.type === COLUMN_TYPES.CHECKLIST) {
                              e.preventDefault();
                              handleCellChecklistBuilderOpen(rowIndex, column.id, e);
                            }
                          }}
                          style={{ 
                            cursor: isReadonly ? 'not-allowed' : 
                                   (column.type === COLUMN_TYPES.CHECKBOX || column.type === COLUMN_TYPES.CHECKLIST || column.type === COLUMN_TYPES.CELL_CHECKLIST) ? 'default' : 'pointer' 
                          }}
                        >
                          {column.type === COLUMN_TYPES.CHECKLIST ? (
                            <div className="checklist-cell">
                              {(() => {
                                // Check if this cell has its own checklist options
                                const cellChecklistOptions = row[`${column.id}_checklistOptions`];
                                const optionsToUse = cellChecklistOptions && cellChecklistOptions.length > 0 
                                  ? cellChecklistOptions 
                                  : (column.checklistOptions || []);
                                
                                return optionsToUse.map((option, optionIndex) => {
                                  const cellValue = row[column.id] || [];
                                  const isChecked = cellValue[optionIndex] === true;
                                  return (
                                    <div key={optionIndex} className="checklist-item">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleChecklistItemToggle(rowIndex, column.id, optionIndex)}
                                        disabled={isReadonly}
                                        className="Engtable-checklist-checkbox"
                                      />
                                      <span className="checklist-item-label">
                                        {option || `Option ${optionIndex + 1}`}
                                      </span>
                                    </div>
                                  );
                                });
                              })()}
                              {(() => {
                                const cellChecklistOptions = row[`${column.id}_checklistOptions`];
                                const columnOptions = column.checklistOptions || [];
                                const hasNoOptions = (!cellChecklistOptions || cellChecklistOptions.length === 0) && 
                                                   (!columnOptions || columnOptions.length === 0);
                                
                                return hasNoOptions && (
                                  <div className="checklist-empty">
                                    {/* Show blank box instead of text */}
                                  </div>
                                );
                              })()}
                              {/* Cell-specific checklist indicator */}
                              {row[`${column.id}_checklistOptions`] && (
                                <div className="cell-checklist-indicator">
                                  <small>✓ Custom options</small>
                                </div>
                              )}
                            </div>
                          ) : column.type === COLUMN_TYPES.CELL_CHECKLIST ? (
                            <div className="cell-checklist-cell">
                              {(() => {
                                // For cell-specific checklist columns, check if this cell has its own options
                                const cellChecklistOptions = row[`${column.id}_checklistOptions`];
                                
                                if (cellChecklistOptions && cellChecklistOptions.length > 0) {
                                  // Cell has custom options - render them as checklists
                                  return cellChecklistOptions.map((option, optionIndex) => {
                                    const cellValue = row[column.id] || '';
                                    const selectedOptions = cellValue ? cellValue.split(',').map(opt => opt.trim()) : [];
                                    const isChecked = selectedOptions.includes(option);
                                    return (
                                      <div key={optionIndex} className="checklist-item">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => handleCellChecklistTextToggle(rowIndex, column.id, option, optionIndex)}
                                          disabled={isReadonly}
                                          className="Engtable-checklist-checkbox"
                                        />
                                        <span className="checklist-item-label">
                                          {option || `Option ${optionIndex + 1}`}
                                        </span>
                                      </div>
                                    );
                                  });
                                } else {
                                  // Cell has no custom options - show blank box like other columns
                                  return (
                                    <span className="cell-value">
                                      {row[column.id] || <em>Click to edit</em>}
                                    </span>
                                  );
                                }
                              })()}
                            </div>
                          ) : column.type === COLUMN_TYPES.CHECKBOX ? (
                            <div className="checkbox-cell">
                              <input
                                type="checkbox"
                                checked={row[column.id] === true || row[column.id] === 'true'}
                                onChange={(e) => handleCellChange(rowIndex, column.id, e.target.checked)}
                                disabled={isReadonly}
                                className="Engtable-checkbox-input"
                              />
                              {column.checkboxLabel && (
                                <span className="checkbox-label-text">
                                  {column.checkboxLabel}
                                </span>
                              )}
                            </div>
                          ) : isEditing ? (
                            <Input
                              type={column.type === COLUMN_TYPES.NUMBER ? 'number' : 'text'}
                              value={row[column.id] || ''}
                              onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
                              onBlur={handleCellBlur}
                              onKeyDown={(e) => e.key === 'Enter' && handleCellBlur()}
                              autoFocus
                              className="Engtable-cell-input"
                            />
                          ) : (
                            <span className="cell-value">
                              {row[column.id] || <em>Click to edit</em>}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Form Actions */}
        <div className="table-builder-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
          >
            {isLoading 
              ? (editMode ? 'Updating...' : 'Creating...') 
              : (editMode ? 'Update Table' : 'Create Table')
            }
          </Button>
        </div>
      </form>

      {/* Cell-specific Checklist Builder Popup */}
      {cellChecklistBuilder && (
        <div className="cell-checklist-builder-overlay" onClick={handleCellChecklistBuilderClose}>
          <div className="cell-checklist-builder-popup" onClick={(e) => e.stopPropagation()}>
            <div className="cell-checklist-builder-header">
              <h4>Cell-Specific Checklist Builder</h4>
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleCellChecklistBuilderClose}
              >
                ×
              </Button>
            </div>
            
            <div className="cell-checklist-builder-content">
              <p className="cell-checklist-builder-info">
                <small>Row {cellChecklistBuilder.rowIndex + 1}, Column "{columnHeaders.find(col => col.id === cellChecklistBuilder.columnId)?.label}"</small>
              </p>
              
              <div className="cell-checklist-options">
                <div className="cell-checklist-options-header">
                  <span>Checklist Options:</span>
                  <Button
                    type="button"
                    variant="add"
                    size="small"
                    onClick={handleCellChecklistOptionAdd}
                  >
                    Add Option
                  </Button>
                </div>
                
                {cellChecklistBuilder.options.map((option, optionIndex) => (
                  <div key={optionIndex} className="cell-checklist-option-item">
                    <Input
                      type="text"
                      value={option}
                      onChange={(e) => handleCellChecklistOptionChange(optionIndex, e.target.value)}
                      placeholder={`Option ${optionIndex + 1}`}
                      className="cell-checklist-option-input"
                    />
                    <Button
                      type="button"
                      variant="remove"
                      size="small"
                      onClick={() => handleCellChecklistOptionRemove(optionIndex)}
                      title="Remove option"
                    >
                      ×
                    </Button>
                  </div>
                ))}
                
                {cellChecklistBuilder.options.length === 0 && (
                  <div className="cell-checklist-empty-state">
                    <em>Click "Add Option" to create checklist items</em>
                  </div>
                )}
              </div>
            </div>
            
            <div className="cell-checklist-builder-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCellChecklistBuilderClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleCellChecklistSave}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableBuilder;
